import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import CommandPalette from './CommandPalette';
import { NAV } from '../config/nav';
import {
  ChevronDown, Search, LogOut, Menu, X,
  Bell, Sun, Moon, User,
} from 'lucide-react';

// ── Nav leaf ──────────────────────────────────────────────────────────────
function NavLeaf({ item, onClose }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClose}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] font-medium transition-colors duration-100
         ${isActive
           ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400'
           : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'}`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-[6px] bottom-[6px] w-[2.5px] bg-primary-500 rounded-full" />
          )}
          <item.Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

// ── Nav group ─────────────────────────────────────────────────────────────
function NavGroup({ item, onClose }) {
  const location      = useLocation();
  const isChildActive = item.children.some(c => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [location.pathname, isChildActive]);

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full relative flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] font-medium transition-colors duration-100
          ${isChildActive
            ? 'text-primary-700 dark:text-primary-400'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
      >
        {isChildActive && (
          <span className="absolute left-0 top-[6px] bottom-[6px] w-[2.5px] bg-primary-500 rounded-full" />
        )}
        <item.Icon size={15} strokeWidth={isChildActive ? 2.2 : 1.8} />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown
          size={12}
          className={`transition-transform duration-200 shrink-0 ${open ? 'rotate-180' : ''} ${
            isChildActive ? 'text-primary-400' : 'text-slate-300 dark:text-slate-600'
          }`}
        />
      </button>

      {open && (
        <div className="ml-[26px] mt-0.5 mb-1 border-l border-slate-100 dark:border-slate-700 pl-3 space-y-px">
          {item.children.map(child => (
            <NavLink
              key={child.to}
              to={child.to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-2 px-2.5 py-[6px] rounded-lg text-[12.5px] font-medium transition-colors duration-100
                 ${isActive
                   ? 'text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10'
                   : 'text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
              }
            >
              {({ isActive }) => (
                <>
                  <child.Icon size={13} strokeWidth={isActive ? 2.2 : 1.8} />
                  {child.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────
function Sidebar({ onClose, user }) {
  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900 border-r border-slate-200/70 dark:border-slate-800">

      {/* Logo */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 dark:border-slate-800 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-primary-500 rounded-lg flex items-center justify-center shrink-0">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
              <rect x="1"   y="1"   width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.95" />
              <rect x="7.5" y="1"   width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.40" />
              <rect x="1"   y="7.5" width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.40" />
              <rect x="7.5" y="7.5" width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.95" />
            </svg>
          </div>
          <div>
            <p className="text-slate-900 dark:text-white font-bold text-[13px] leading-none tracking-tight">Arecore</p>
            <p className="text-primary-400 text-[10px] font-bold uppercase tracking-widest mt-[3px] leading-none">ERP</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 lg:hidden p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Navegación */}
      <nav className="flex-1 px-2 py-3 space-y-px overflow-y-auto">
        {NAV.map(item =>
          item.to
            ? <NavLeaf  key={item.id} item={item} onClose={onClose} />
            : <NavGroup key={item.id} item={item} onClose={onClose} />
        )}
      </nav>

    </div>
  );
}

// ── Layout principal ───────────────────────────────────────────────────────
export default function Layout() {
  const { user, logout }          = useAuth();
  const { isDark, toggleTheme }   = useTheme();
  const navigate                  = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Atajo ⌘K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(p => !p);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Cerrar user menu al hacer click afuera
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const firstName = user?.nombre?.split(' ')[0] ?? '';

  return (
    <div className="flex h-screen bg-surface dark:bg-slate-950 overflow-hidden">

      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-56 flex-col shrink-0">
        <Sidebar user={user} />
      </aside>

      {/* Sidebar mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-56 flex flex-col shadow-dropdown">
            <Sidebar onClose={() => setSidebarOpen(false)} user={user} />
          </aside>
        </div>
      )}

      {/* Columna derecha */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* ── Top bar ── */}
        <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-4 lg:px-5 h-14 flex items-center gap-3 shrink-0">

          {/* Hamburger mobile */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-1.5 -ml-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors"
          >
            <Menu size={17} />
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Search — abre CommandPalette */}
          <button
            onClick={() => setPaletteOpen(true)}
            className="hidden sm:flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all duration-150 w-[240px]"
          >
            <Search size={13} className="shrink-0" />
            <span className="text-[13px] flex-1 text-left leading-none select-none">Buscar...</span>
            <span className="flex items-center gap-[3px] shrink-0">
              <kbd className="inline-flex items-center justify-center text-[10px] font-mono bg-white dark:bg-slate-700 w-5 h-5 rounded ring-1 ring-slate-200 dark:ring-slate-600 text-slate-300 dark:text-slate-500">
                ⌘
              </kbd>
              <kbd className="inline-flex items-center justify-center text-[10px] font-mono bg-white dark:bg-slate-700 w-5 h-5 rounded ring-1 ring-slate-200 dark:ring-slate-600 text-slate-300 dark:text-slate-500">
                K
              </kbd>
            </span>
          </button>

          {/* Acciones */}
          <div className="flex items-center gap-1.5">

            {/* Notificaciones */}
            <button
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              title="Notificaciones"
            >
              <Bell size={15} />
            </button>

            {/* Modo oscuro/claro */}
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              title={isDark ? 'Modo claro' : 'Modo oscuro'}
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {/* Usuario + dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 select-none">
                  {user?.nombre?.[0]?.toUpperCase() ?? <User size={12} />}
                </div>
                <span className="hidden sm:block text-[13px] font-semibold text-slate-700 dark:text-slate-300 leading-none">
                  {firstName}
                </span>
                <ChevronDown
                  size={12}
                  className={`text-slate-400 dark:text-slate-500 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-slate-900 rounded-xl shadow-dropdown border border-slate-100 dark:border-slate-800 py-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-100">
                  <div className="px-3.5 py-2.5 border-b border-slate-100 dark:border-slate-800">
                    <p className="text-[13px] font-bold text-slate-900 dark:text-white truncate">{user?.nombre}</p>
                    <p className="text-[11px] text-slate-400 capitalize mt-0.5">{user?.rol}</p>
                  </div>
                  <div className="pt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] text-slate-500 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]"
                    >
                      <LogOut size={13} />
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Contenido */}
        <main className="flex-1 overflow-y-auto p-5 lg:p-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
